/**
 * CompactClassicAnalysis - Modernized single-viewport video analysis
 * Features:
 * - Compact layout with file list, preview, and settings in one view
 * - Frame-by-frame video preview with timeline
 * - Analysis mode selector (shared prompts from expertPersonas)
 * - Inline folder dropdown
 * - Glassmorphism dark mode design
 * - Matrix rain visualization during processing (January 2026)
 * - Inline report display with prestige reveal animation
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Upload, Youtube, FolderOpen, Play, Pause, X, Plus,
  ChevronLeft, ChevronRight, Maximize2, FileVideo, FileSpreadsheet,
  Cloud, Loader, AlertTriangle, CheckCircle2, Settings2, Sparkles,
  RotateCcw, Zap, Target, Eye, ArrowLeft, FileText, Download
} from 'lucide-react';
import { useTheme } from '../../../utils/theme';
import FolderDropdown from '../../FolderDropdown';
import OneDrivePicker from '../../OneDrivePicker';
import { MatrixBackground } from './MatrixBackground';
import { StructuredAnalysisReport } from './StructuredAnalysisReport';
import { MediaCard } from './MediaCard';
import { analyzeMedia, GEMINI_MODELS } from '../../../lib/geminiService';
import { MediaType } from '../../../lib/types';
import { useSportContext } from '../../../hooks/useSportContext';
import {
  VIDEO_SIZE_LIMITS,
  analyzeVideoFile,
  compressVideo,
  validateVideoForGemini,
  getVideoMetadata
} from '../../../lib/videoCompressionService';
import { getModesBySport, getModeConfig, getDefaultPrompt, isDeepAnalysisMode } from '../../../lib/analysisModes';
import { generateDeepAnalysisPDF } from '../../../lib/deepAnalysisPdfGenerator';
import { useTeamConfig } from '../../../lib/teamConfig';

// Icon mapping for analysis modes (centralized config uses string identifiers)
const ICON_MAP = {
  baseball: Target,
  bat: Zap,
  basketball: Eye,
  sparkles: Sparkles,
  target: Target,
  zap: Zap,
  eye: Eye,
  document: FileText
};

/**
 * Frame-by-frame video preview component
 */
const VideoPreviewPlayer = ({ file, previewUrl, onRemove }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [frameNumber, setFrameNumber] = useState(0);

  // Frame skip amount (0.033s for ~30fps)
  const FRAME_STEP = 0.033;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      setFrameNumber(Math.round(video.currentTime * 30));
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [previewUrl]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const skipFrame = useCallback((direction) => {
    const video = videoRef.current;
    if (!video) return;

    const skipAmount = direction === 'next' ? FRAME_STEP : -FRAME_STEP;
    const newTime = Math.max(0, Math.min(duration, video.currentTime + skipAmount));
    video.currentTime = newTime;
    video.pause();
    setIsPlaying(false);
  }, [duration]);

  const seekTo = useCallback((percent) => {
    const video = videoRef.current;
    if (!video || duration === 0) return;

    const newTime = percent * duration;
    video.currentTime = newTime;
  }, [duration]);

  const handleProgressClick = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    seekTo(percent);
  }, [seekTo]);

  const restart = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    setCurrentTime(0);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="h-full flex flex-col rounded-xl overflow-hidden border border-white/10"
      style={{
        background: 'rgba(13, 13, 18, 0.8)',
        backdropFilter: 'blur(12px)'
      }}
    >
      {/* Video Container */}
      <div className="relative flex-1 bg-black min-h-0">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader className="w-8 h-8 text-[#FFD700] animate-spin" />
          </div>
        )}

        <video
          ref={videoRef}
          src={previewUrl}
          className="w-full h-full object-contain"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          playsInline
          muted
        />

        {/* Click to play overlay */}
        <button
          onClick={togglePlay}
          className="absolute inset-0 cursor-pointer focus:outline-none"
        />

        {/* Frame counter badge */}
        <div className="absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-mono"
          style={{ background: 'rgba(0, 0, 0, 0.8)', color: '#FFD700' }}
        >
          Frame {frameNumber}
        </div>

        {/* Remove button */}
        {onRemove && (
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500/90 text-white hover:bg-red-600 transition-colors"
          >
            <X size={14} />
          </button>
        )}

        {/* Center play icon when paused */}
        {!isPlaying && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="rounded-full p-4" style={{ background: 'rgba(255, 215, 0, 0.9)' }}>
              <Play size={24} className="text-black ml-0.5" />
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="px-3 py-2 bg-black/50">
        <div
          className="relative h-2 cursor-pointer rounded-full overflow-hidden"
          style={{ background: 'rgba(255, 255, 255, 0.1)' }}
          onClick={handleProgressClick}
        >
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #FFD700, #FFC000)' }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 font-mono mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="px-3 py-2 border-t border-white/10 flex items-center justify-between"
        style={{ background: 'rgba(0, 0, 0, 0.5)' }}
      >
        {/* Left controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={togglePlay}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={16} className="text-white" /> : <Play size={16} className="text-white" />}
          </button>
          <button
            onClick={restart}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            title="Restart"
          >
            <RotateCcw size={16} className="text-white" />
          </button>
        </div>

        {/* Frame stepping */}
        <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: 'rgba(255, 215, 0, 0.1)' }}>
          <button
            onClick={() => skipFrame('prev')}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
            title="Previous frame (33ms)"
          >
            <ChevronLeft size={16} style={{ color: '#FFD700' }} />
          </button>
          <span className="text-xs font-mono px-2" style={{ color: '#FFD700' }}>Frame</span>
          <button
            onClick={() => skipFrame('next')}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
            title="Next frame (33ms)"
          >
            <ChevronRight size={16} style={{ color: '#FFD700' }} />
          </button>
        </div>

        {/* Fullscreen */}
        <button
          onClick={() => videoRef.current?.requestFullscreen()}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          title="Fullscreen"
        >
          <Maximize2 size={16} className="text-white" />
        </button>
      </div>
    </div>
  );
};

/**
 * File list item component
 */
const FileListItem = ({ file, preview, isSelected, onSelect, onRemove, index }) => {
  const isVideo = file.type?.startsWith('video/') || ['mp4', 'mov', 'avi'].includes(file.name?.split('.').pop()?.toLowerCase());
  const isCsv = file.name?.endsWith('.csv');

  return (
    <div
      onClick={() => onSelect(index)}
      className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-all duration-200 border ${
        isSelected
          ? 'border-[#FFD700]/50'
          : 'border-transparent hover:border-white/10'
      }`}
      style={{
        background: isSelected ? 'rgba(255, 215, 0, 0.08)' : 'rgba(255, 255, 255, 0.02)'
      }}
    >
      {/* Thumbnail */}
      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 relative"
        style={{ background: isCsv ? 'rgba(16, 185, 129, 0.1)' : 'rgba(0, 0, 0, 0.3)' }}
      >
        {isVideo && preview ? (
          <video src={preview} className="w-full h-full object-cover" muted />
        ) : isCsv ? (
          <div className="w-full h-full flex items-center justify-center">
            <FileSpreadsheet size={20} style={{ color: '#10b981' }} />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FileVideo size={20} className="text-gray-400" />
          </div>
        )}
        {file.isOneDrive && (
          <div className="absolute -top-1 -right-1 p-0.5 rounded-full bg-blue-500">
            <Cloud size={8} className="text-white" />
          </div>
        )}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-white truncate">{file.name}</div>
        <div className="text-xs text-gray-500">
          {file.size ? formatFileSize(file.size) : 'Unknown size'}
        </div>
      </div>

      {/* Remove button */}
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(index); }}
        className="p-1.5 rounded-lg hover:bg-red-500/20 transition-colors flex-shrink-0"
      >
        <X size={14} className="text-red-400" />
      </button>
    </div>
  );
};

// Helper function
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Prestige Reveal Animation Component
 * Creates a dramatic reveal effect when analysis completes
 */
const PrestigeReveal = ({ isVisible, onComplete, children }) => {
  const [phase, setPhase] = useState('hidden'); // 'hidden' | 'flash' | 'reveal' | 'complete'

  useEffect(() => {
    if (isVisible && phase === 'hidden') {
      // Start the prestige sequence
      setPhase('flash');

      // Flash phase - bright light effect
      setTimeout(() => setPhase('reveal'), 400);

      // Reveal phase - content fades in
      setTimeout(() => {
        setPhase('complete');
        onComplete?.();
      }, 1200);
    } else if (!isVisible) {
      setPhase('hidden');
    }
  }, [isVisible, phase, onComplete]);

  if (phase === 'hidden') return null;

  return (
    <div className="relative">
      {/* Flash overlay */}
      {phase === 'flash' && (
        <div
          className="fixed inset-0 z-50 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at center, rgba(255, 215, 0, 0.8) 0%, transparent 70%)',
            animation: 'flashPulse 0.4s ease-out forwards'
          }}
        />
      )}

      {/* Content with reveal animation */}
      <div
        className={`transition-all duration-700 ease-out ${
          phase === 'reveal' || phase === 'complete'
            ? 'opacity-100 transform translate-y-0'
            : 'opacity-0 transform translate-y-8'
        }`}
      >
        {children}
      </div>

      {/* CSS Keyframes */}
      <style>{`
        @keyframes flashPulse {
          0% { opacity: 0; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
          100% { opacity: 0; transform: scale(1.5); }
        }
      `}</style>
    </div>
  );
};

/**
 * Main CompactClassicAnalysis component
 */
const CompactClassicAnalysis = ({
  onAnalyze,
  isAnalyzing: externalIsAnalyzing = false,
  allPlayers = [],
  onOneDriveSelect,
  acceptedFileTypes = ['csv', 'mp4', 'mov']
}) => {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const fileInputRef = useRef(null);
  const { config: teamConfig } = useTeamConfig();

  // Sport context for Matrix background and mode filtering
  const { sportCode, isBaseball, isBasketball } = useSportContext();
  const sportCategory = isBaseball ? 'baseball' : isBasketball ? 'basketball' : 'basketball';

  // Get analysis modes from centralized config, filtered by sport
  const filteredModes = getModesBySport(sportCategory).map(mode => ({
    ...mode,
    icon: ICON_MAP[mode.icon] || Sparkles // Map string icon to component
  }));

  // Default mode based on sport
  const defaultMode = isBaseball ? 'pitch-tipping' : 'basketball-analysis';

  // State
  const [inputType, setInputType] = useState('upload'); // 'upload' | 'youtube'
  const [videoFiles, setVideoFiles] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [csvFiles, setCsvFiles] = useState([]);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [analysisMode, setAnalysisMode] = useState(defaultMode);
  const [folderId, setFolderId] = useState(null);
  const [playerId, setPlayerId] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [error, setError] = useState(null);

  // Analysis state - for inline analysis with Matrix background
  const [isLocalAnalyzing, setIsLocalAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showPrestige, setShowPrestige] = useState(false);
  const [viewMode, setViewMode] = useState('upload'); // 'upload' | 'single' | 'multi' | 'report'
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfGenerated, setPdfGenerated] = useState(false);

  // Multi-video grid state (MoE-style)
  const [thumbnailScale, setThumbnailScale] = useState(100);
  const [showGrid, setShowGrid] = useState(true);
  const [videoStartPoints, setVideoStartPoints] = useState(new Map());
  const videoRefs = useRef(new Map());

  // Combined analyzing state
  const isAnalyzing = externalIsAnalyzing || isLocalAnalyzing;

  // Reset analysis mode when sport changes
  useEffect(() => {
    const newDefault = isBaseball ? 'pitch-tipping' : 'basketball-analysis';
    setAnalysisMode(newDefault);
  }, [sportCategory, isBaseball]);

  // Auto-detect multi-video mode when files are uploaded
  useEffect(() => {
    if (viewMode === 'report') return; // Don't change if viewing report
    if (videoFiles.length === 0) {
      setViewMode('upload');
    } else if (videoFiles.length === 1) {
      setViewMode('single');
    } else {
      setViewMode('multi');
    }
  }, [videoFiles.length, viewMode]);

  // Calculate optimal grid columns based on item count and thumbnail scale
  const getGridStyle = useCallback(() => {
    const scale = thumbnailScale / 100;
    const baseSize = 200;
    const scaledSize = Math.round(baseSize * scale);

    return {
      gridTemplateColumns: `repeat(auto-fill, minmax(${Math.max(scaledSize, 100)}px, ${scaledSize}px))`,
      gap: `${Math.max(4, Math.round(12 * scale))}px`,
      justifyContent: 'center',
    };
  }, [thumbnailScale]);

  // Convert uploaded files to MediaCard-compatible items
  const getMediaItems = useCallback(() => {
    return videoFiles.map((file, index) => ({
      id: `video-${index}`,
      name: file.name,
      type: MediaType.VIDEO,
      previewUrl: videoPreviews[index],
      file,
      size: file.size
    }));
  }, [videoFiles, videoPreviews]);

  // Handle video ref callback for MediaCard
  const handleVideoRef = useCallback((id, el) => {
    if (el) {
      videoRefs.current.set(id, el);
    } else {
      videoRefs.current.delete(id);
    }
  }, []);

  // Handle start point change for a video
  const handleStartPointChange = useCallback((id, seconds) => {
    setVideoStartPoints(prev => new Map(prev).set(id, seconds));
  }, []);

  // Handle file selection
  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    if (e.target) e.target.value = '';
  }, []);

  const processFiles = useCallback((files) => {
    const newVideos = [];
    const newPreviews = [];
    const newCsvs = [];

    files.forEach((file) => {
      const ext = file.name.split('.').pop().toLowerCase();

      if (['mp4', 'mov', 'avi'].includes(ext) || file.type?.startsWith('video/')) {
        newVideos.push(file);
        newPreviews.push(URL.createObjectURL(file));
      } else if (ext === 'csv') {
        if (csvFiles.length === 0 && newCsvs.length === 0) {
          newCsvs.push(file);
        }
      }
    });

    if (newVideos.length > 0) {
      setVideoFiles(prev => [...prev, ...newVideos]);
      setVideoPreviews(prev => [...prev, ...newPreviews]);
    }
    if (newCsvs.length > 0) {
      setCsvFiles(newCsvs);
    }
  }, [csvFiles.length]);

  // Handle drag and drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  }, [processFiles]);

  // Remove file
  const handleRemoveFile = useCallback((index) => {
    URL.revokeObjectURL(videoPreviews[index]);
    setVideoFiles(prev => prev.filter((_, i) => i !== index));
    setVideoPreviews(prev => prev.filter((_, i) => i !== index));
    if (selectedFileIndex >= videoFiles.length - 1) {
      setSelectedFileIndex(Math.max(0, videoFiles.length - 2));
    }
  }, [videoPreviews, selectedFileIndex, videoFiles.length]);

  const handleRemoveCsv = useCallback(() => {
    setCsvFiles([]);
  }, []);

  // Convert file to base64 for Gemini analysis
  const fileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Compression progress state
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);

  // Handle analysis - now performs inline Gemini analysis with Matrix background
  // Includes automatic video compression for oversized files
  const handleStartAnalysis = useCallback(async () => {
    if (!folderId) {
      setError('Please select a folder');
      return;
    }

    if (inputType === 'upload' && videoFiles.length === 0) {
      setError('Please upload at least one video');
      return;
    }

    if (inputType === 'youtube' && !youtubeUrl) {
      setError('Please enter a YouTube URL');
      return;
    }

    setError(null);
    setIsLocalAnalyzing(true);
    setAnalysisResult(null);
    setShowPrestige(false);
    setCompressionProgress(0);

    try {
      // Prepare media items for Gemini analysis
      const mediaItems = [];

      // Process video files - check sizes and compress if needed
      for (let i = 0; i < videoFiles.length; i++) {
        let file = videoFiles[i];
        const analysis = analyzeVideoFile(file);

        console.log(`[CompactClassicAnalysis] Video ${file.name}: ${analysis.sizeInMB} MB, needs compression: ${analysis.needsCompression}`);

        // Check if file exceeds absolute maximum (2 GB)
        if (analysis.exceedsFileApiLimit) {
          setError(`${file.name} exceeds the 2 GB maximum. Please use a smaller file.`);
          setIsLocalAnalyzing(false);
          return;
        }

        // Compress if file exceeds 100 MB
        if (analysis.needsCompression) {
          console.log(`[CompactClassicAnalysis] Compressing ${file.name}...`);
          setIsCompressing(true);

          try {
            file = await compressVideo(file, {
              onProgress: (progress) => {
                setCompressionProgress(progress);
              },
              targetSizeMB: 80,
              quality: 'medium'
            });
            console.log(`[CompactClassicAnalysis] Compressed to ${(file.size / 1024 / 1024).toFixed(2)} MB`);
          } catch (compressError) {
            console.warn('[CompactClassicAnalysis] Compression failed, using original:', compressError);
            // Continue with original file if compression fails
          }

          setIsCompressing(false);
          setCompressionProgress(0);
        }

        const base64Data = await fileToBase64(file);
        mediaItems.push({
          file,
          type: MediaType.VIDEO,
          base64Data,
          mimeType: file.type || 'video/mp4',
          name: file.name,
          size: file.size
        });
      }

      // Add CSV files if present
      for (const file of csvFiles) {
        const base64Data = await fileToBase64(file);
        mediaItems.push({
          file,
          type: MediaType.CSV,
          base64Data,
          mimeType: 'text/csv',
          name: file.name
        });
      }

      console.log('[CompactClassicAnalysis] Starting Gemini analysis with mode:', analysisMode);

      // Perform inline Gemini analysis
      const result = await analyzeMedia({
        mediaItems,
        analysisMode,
        model: 'pro', // Use Gemini 3 Pro for thorough analysis
        contextFiles: []
      });

      console.log('[CompactClassicAnalysis] Analysis complete');
      setAnalysisResult(result);

      // Trigger prestige reveal
      setShowPrestige(true);
      setViewMode('report');

      // Generate PDF if this is a deep analysis mode
      if (isDeepAnalysisMode(analysisMode)) {
        console.log('[CompactClassicAnalysis] Deep Analysis mode - generating PDF...');
        setIsGeneratingPDF(true);
        setPdfGenerated(false);

        try {
          await generateDeepAnalysisPDF(result, {
            title: `Deep Analysis - ${videoFiles[0]?.name || 'Video'}`,
            sport: sportCategory,
            teamConfig,
            videoName: videoFiles[0]?.name,
            analysisDate: new Date()
          });
          setPdfGenerated(true);
          console.log('[CompactClassicAnalysis] PDF generated successfully');
        } catch (pdfError) {
          console.error('[CompactClassicAnalysis] PDF generation failed:', pdfError);
          // Don't fail the whole analysis if PDF fails
        } finally {
          setIsGeneratingPDF(false);
        }
      }

      // Also call parent handler to save to backend/folder
      onAnalyze?.({
        inputType,
        videoFiles,
        csvFiles,
        youtubeUrl,
        folderId,
        playerId,
        analysisMode,
        customInstructions
      });

    } catch (err) {
      console.error('[CompactClassicAnalysis] Analysis error:', err);
      setError(err.message || 'Analysis failed');
    } finally {
      setIsLocalAnalyzing(false);
      setIsCompressing(false);
    }
  }, [inputType, videoFiles, csvFiles, youtubeUrl, folderId, playerId, analysisMode, customInstructions, onAnalyze, fileToBase64, sportCategory, teamConfig]);

  // Reset to upload view
  const handleBackToUpload = useCallback(() => {
    setViewMode('upload');
    setAnalysisResult(null);
    setShowPrestige(false);
    setPdfGenerated(false);
  }, []);

  // Handler to re-download PDF
  const handleDownloadPDF = useCallback(async () => {
    if (!analysisResult) return;

    setIsGeneratingPDF(true);
    try {
      await generateDeepAnalysisPDF(analysisResult, {
        title: `Deep Analysis - ${videoFiles[0]?.name || 'Video'}`,
        sport: sportCategory,
        teamConfig,
        videoName: videoFiles[0]?.name,
        analysisDate: new Date()
      });
    } catch (err) {
      console.error('[CompactClassicAnalysis] PDF re-download failed:', err);
    } finally {
      setIsGeneratingPDF(false);
    }
  }, [analysisResult, videoFiles, sportCategory, teamConfig]);

  // Get current analysis mode info
  const currentMode = filteredModes.find(m => m.id === analysisMode) || filteredModes[0];
  const ModeIcon = currentMode.icon;

  // If we have a report to show, render the report view
  if (viewMode === 'report' && analysisResult) {
    const isDeepMode = isDeepAnalysisMode(analysisMode);

    return (
      <div className="h-full flex flex-col relative" style={{ background: 'var(--color-background)' }}>
        {/* Report Header */}
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between relative z-50"
          style={{ background: 'rgba(13, 13, 18, 0.8)', backdropFilter: 'blur(12px)' }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToUpload}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              <ArrowLeft size={14} />
              New Analysis
            </button>
            <div className="h-4 w-px bg-white/20" />
            <div className="flex items-center gap-2">
              <ModeIcon size={16} style={{ color: currentMode.color }} />
              <span className="text-sm font-medium text-white">{currentMode.name}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* PDF status and re-download for deep analysis */}
            {isDeepMode && (
              <>
                {isGeneratingPDF ? (
                  <div className="flex items-center gap-2 text-indigo-400">
                    <Loader size={14} className="animate-spin" />
                    <span className="text-sm">Generating PDF...</span>
                  </div>
                ) : pdfGenerated ? (
                  <button
                    onClick={handleDownloadPDF}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border border-indigo-500/30 hover:bg-indigo-500/20"
                    style={{ color: '#818cf8' }}
                  >
                    <Download size={14} />
                    Download PDF
                  </button>
                ) : null}
              </>
            )}
            <div className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-green-400" />
              <span className="text-sm text-green-400">Analysis Complete</span>
            </div>
          </div>
        </div>

        {/* Report Content with Prestige Reveal */}
        <div className="flex-1 overflow-y-auto p-6">
          <PrestigeReveal isVisible={showPrestige} onComplete={() => console.log('Prestige reveal complete')}>
            <div className="max-w-4xl mx-auto">
              <StructuredAnalysisReport markdown={analysisResult} analysisMode={analysisMode} mediaItems={videoFiles} title={`${currentMode?.name || 'Video'} Analysis`} />
            </div>
          </PrestigeReveal>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col relative" style={{ background: 'var(--color-background)' }}>
      {/* Matrix Background - shown during analysis */}
      <MatrixBackground isActive={isAnalyzing} phase="analyzing" sportCategory={sportCategory} />

      {/* Floating Controls - Left side */}
      <div className="absolute top-4 left-4 z-50 flex items-center gap-2">
        {/* Input type toggle - floating pill */}
        <div
          className="flex items-center gap-1 p-1 rounded-full backdrop-blur-xl"
          style={{
            background: 'rgba(13, 13, 18, 0.85)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        >
          <button
            onClick={() => setInputType('upload')}
            className="p-2.5 rounded-full transition-all"
            style={inputType === 'upload' ? {
              background: 'linear-gradient(135deg, #FFD700, #FFC000)',
              boxShadow: '0 0 16px rgba(255, 215, 0, 0.4)'
            } : {}}
            title="Upload Video"
          >
            <Upload size={16} className={inputType === 'upload' ? 'text-black' : 'text-gray-400'} />
          </button>
          <button
            onClick={() => setInputType('youtube')}
            className="p-2.5 rounded-full transition-all"
            style={inputType === 'youtube' ? {
              background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
              boxShadow: '0 0 16px rgba(220, 38, 38, 0.4)'
            } : {}}
            title="YouTube URL"
          >
            <Youtube size={16} className={inputType === 'youtube' ? 'text-white' : 'text-gray-400'} />
          </button>
        </div>

        {/* Analysis mode selector - floating */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-full backdrop-blur-xl cursor-pointer"
          style={{
            background: 'rgba(13, 13, 18, 0.85)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        >
          <ModeIcon size={16} style={{ color: currentMode.color }} />
          <select
            value={analysisMode}
            onChange={(e) => setAnalysisMode(e.target.value)}
            className="bg-transparent text-sm text-white border-none focus:outline-none cursor-pointer pr-1"
          >
            {filteredModes.map(mode => (
              <option key={mode.id} value={mode.id} className="bg-gray-900">
                {mode.name}
              </option>
            ))}
          </select>
        </div>

        {/* Deep Analysis indicator - floating badge */}
        {isDeepAnalysisMode(analysisMode) && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-full backdrop-blur-xl"
            style={{
              background: 'rgba(99, 102, 241, 0.2)',
              border: '1px solid rgba(99, 102, 241, 0.4)',
              boxShadow: '0 0 16px rgba(99, 102, 241, 0.2)'
            }}
          >
            <FileText size={14} className="text-indigo-400" />
            <span className="text-xs text-indigo-300 font-medium">PDF</span>
          </div>
        )}
      </div>

      {/* Floating Controls - Right side */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
        {/* Folder dropdown - floating */}
        <div
          className="rounded-full backdrop-blur-xl overflow-hidden"
          style={{
            background: 'rgba(13, 13, 18, 0.85)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div className="w-44">
            <FolderDropdown
              selectedFolderId={folderId}
              onFolderSelect={setFolderId}
              compact={true}
            />
          </div>
        </div>

        {/* Player selector - floating (only if players exist) */}
        {allPlayers.length > 0 && (
          <div
            className="rounded-full backdrop-blur-xl overflow-hidden"
            style={{
              background: 'rgba(13, 13, 18, 0.85)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}
          >
            <select
              value={playerId}
              onChange={(e) => setPlayerId(e.target.value)}
              className="px-3 py-2 text-sm bg-transparent text-white focus:outline-none cursor-pointer"
              style={{ maxWidth: '140px' }}
            >
              <option value="" className="bg-gray-900">Player</option>
              {allPlayers.map((player) => (
                <option key={player.PlayerID || player.id} value={player.player_name || player.Player} className="bg-gray-900">
                  {player.player_name || player.Player}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Analyze button - floating action button */}
        <button
          onClick={handleStartAnalysis}
          disabled={isAnalyzing}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold transition-all disabled:opacity-50"
          style={{
            background: isAnalyzing
              ? 'rgba(255, 255, 255, 0.1)'
              : 'linear-gradient(135deg, #FFD700, #FFC000)',
            color: isAnalyzing ? '#999' : 'black',
            boxShadow: isAnalyzing
              ? '0 8px 32px rgba(0, 0, 0, 0.3)'
              : '0 0 24px rgba(255, 215, 0, 0.4), 0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        >
          {isAnalyzing ? (
            <>
              <Loader size={16} className="animate-spin" />
              <span className="text-sm">Analyzing...</span>
            </>
          ) : (
            <>
              <Sparkles size={16} />
              <span className="text-sm">Analyze</span>
            </>
          )}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-3 p-3 rounded-xl border border-red-500/30 flex items-center gap-2"
          style={{ background: 'rgba(239, 68, 68, 0.1)' }}
        >
          <AlertTriangle size={16} className="text-red-400" />
          <span className="text-sm text-red-400">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-red-500/20 rounded">
            <X size={14} className="text-red-400" />
          </button>
        </div>
      )}

      {/* Compression in Progress Overlay */}
      {isCompressing && (
        <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
          <div
            className="px-8 py-6 rounded-2xl border border-[#00BFFF]/30 text-center min-w-[280px]"
            style={{
              background: 'rgba(13, 13, 18, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 0 60px rgba(0, 191, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}
          >
            <div className="relative w-16 h-16 mx-auto mb-4">
              {/* Spinning blue ring for compression */}
              <div
                className="absolute inset-0 rounded-full border-2 border-[#00BFFF]/20"
                style={{
                  borderTopColor: '#00BFFF',
                  animation: 'spin 0.8s linear infinite'
                }}
              />
              {/* Inner pulsing dot */}
              <div
                className="absolute inset-4 rounded-full"
                style={{
                  background: 'radial-gradient(circle, #00BFFF 0%, transparent 70%)',
                  animation: 'pulse 1.5s ease-in-out infinite'
                }}
              />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Compressing Video</h3>
            <p className="text-sm text-gray-400 mb-3">Optimizing for Gemini 3 analysis...</p>

            {/* Progress bar */}
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden mb-2">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${compressionProgress}%`,
                  background: 'linear-gradient(90deg, #00BFFF, #00E5FF)'
                }}
              />
            </div>
            <span className="text-xs text-[#00BFFF]">{compressionProgress}%</span>
          </div>

          {/* CSS Keyframes for spinner */}
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 0.4; transform: scale(0.8); }
              50% { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}

      {/* Analysis in Progress Overlay */}
      {isAnalyzing && !isCompressing && (
        <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
          <div
            className="px-8 py-6 rounded-2xl border border-[#FFD700]/30 text-center"
            style={{
              background: 'rgba(13, 13, 18, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 0 60px rgba(255, 215, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}
          >
            <div className="relative w-16 h-16 mx-auto mb-4">
              {/* Spinning gold ring */}
              <div
                className="absolute inset-0 rounded-full border-2 border-[#FFD700]/20"
                style={{
                  borderTopColor: '#FFD700',
                  animation: 'spin 1s linear infinite'
                }}
              />
              {/* Inner pulsing dot */}
              <div
                className="absolute inset-4 rounded-full"
                style={{
                  background: 'radial-gradient(circle, #FFD700 0%, transparent 70%)',
                  animation: 'pulse 2s ease-in-out infinite'
                }}
              />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Analyzing Video</h3>
            <p className="text-sm text-gray-400">Gemini 3 is processing your footage...</p>
            <div className="flex items-center justify-center gap-1 mt-3">
              <ModeIcon size={14} style={{ color: currentMode.color }} />
              <span className="text-xs" style={{ color: currentMode.color }}>{currentMode.name}</span>
            </div>
          </div>

          {/* CSS Keyframes for spinner */}
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 0.4; transform: scale(0.8); }
              50% { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden p-4 gap-4">
        {inputType === 'upload' ? (
          videoFiles.length === 0 && csvFiles.length === 0 ? (
            /* Single unified drop zone when no files */
            <div
              className={`flex-1 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
                isDragOver ? 'border-[#FFD700]' : 'border-white/20 hover:border-white/30'
              }`}
              style={{ background: isDragOver ? 'rgba(255, 215, 0, 0.05)' : 'rgba(13, 13, 18, 0.6)' }}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
            >
              <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
                style={{ background: 'rgba(255, 215, 0, 0.1)' }}
              >
                <FileVideo size={40} style={{ color: '#FFD700' }} />
              </div>
              <span className="text-xl font-medium text-white mb-2">Drop videos here to analyze</span>
              <span className="text-sm text-gray-400 mb-4">MP4, MOV supported • Frame-by-frame analysis</span>
              <button
                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
                style={{
                  background: 'linear-gradient(135deg, #FFD700, #FFC000)',
                  color: 'black',
                  boxShadow: '0 0 16px rgba(255, 215, 0, 0.3)'
                }}
              >
                Browse Files
              </button>
              <div className="flex items-center gap-4 mt-4">
                <OneDrivePicker
                  onSelect={onOneDriveSelect}
                  acceptedFileTypes={acceptedFileTypes}
                  multiSelect={true}
                  customButtonRender={(onClick) => (
                    <button
                      onClick={(e) => { e.stopPropagation(); onClick(); }}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 hover:bg-white/5 transition-colors"
                    >
                      <Cloud size={16} className="text-blue-400" />
                      <span className="text-sm text-gray-300">OneDrive</span>
                    </button>
                  )}
                />
              </div>
              <input
                type="file"
                ref={fileInputRef}
                accept="video/*,.csv"
                onChange={handleFileChange}
                className="hidden"
                multiple
              />
            </div>
          ) : viewMode === 'multi' ? (
            /* MoE-style grid layout for multiple videos */
            <div className="flex-1 flex flex-col overflow-hidden rounded-xl border border-white/10"
              style={{ background: 'rgba(13, 13, 18, 0.6)', backdropFilter: 'blur(12px)' }}
            >
              {/* Multi-video header with controls */}
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-white">
                    {videoFiles.length} Videos
                  </span>
                  <span className="text-xs text-gray-500">MoE Analysis Mode</span>
                </div>
                <div className="flex items-center gap-3">
                  {/* Thumbnail size slider */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Size</span>
                    <input
                      type="range"
                      min="50"
                      max="150"
                      step="10"
                      value={thumbnailScale}
                      onChange={(e) => setThumbnailScale(parseInt(e.target.value))}
                      className="w-20 h-4 focus:outline-none bg-transparent cursor-pointer accent-[#FFD700]"
                      title="Thumbnail size"
                    />
                    <span className="text-xs font-mono text-gray-400 w-8">{thumbnailScale}%</span>
                  </div>
                  {/* Grid toggle */}
                  <button
                    onClick={() => setShowGrid(!showGrid)}
                    className={`p-1.5 rounded-lg transition-colors ${showGrid ? 'bg-[#FFD700]/20 text-[#FFD700]' : 'text-gray-400 hover:bg-white/10'}`}
                    title="Toggle grid overlay"
                  >
                    <Settings2 size={14} />
                  </button>
                  {/* Add more files */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300 text-xs font-medium transition-colors"
                  >
                    <Plus size={12} />
                    Add
                  </button>
                </div>
              </div>

              {/* Grid container */}
              <div
                className="flex-1 overflow-y-auto p-4"
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
              >
                <div
                  className="grid"
                  style={getGridStyle()}
                >
                  {getMediaItems().map((item, index) => (
                    <div key={item.id} className="relative aspect-video">
                      <MediaCard
                        item={item}
                        onRemove={() => handleRemoveFile(index)}
                        onVideoRef={handleVideoRef}
                        showGrid={showGrid}
                        startPoint={videoStartPoints.get(item.id) || 0}
                        onStartPointChange={handleStartPointChange}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                accept="video/*,.csv"
                onChange={handleFileChange}
                className="hidden"
                multiple
              />
            </div>
          ) : (
          /* Single video mode - original layout with file list + preview */
          <>
            {/* Left panel - File list */}
            <div className="w-64 flex-shrink-0 flex flex-col rounded-xl border border-white/10 overflow-hidden"
              style={{ background: 'rgba(13, 13, 18, 0.6)', backdropFilter: 'blur(12px)' }}
            >
              {/* File list header */}
              <div className="px-3 py-2.5 border-b border-white/10 flex items-center justify-between">
                <span className="text-sm font-medium text-white">
                  Files ({videoFiles.length + csvFiles.length})
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                    title="Add files"
                  >
                    <Plus size={14} className="text-gray-400" />
                  </button>
                  <OneDrivePicker
                    onSelect={onOneDriveSelect}
                    acceptedFileTypes={acceptedFileTypes}
                    multiSelect={true}
                    customButtonRender={(onClick) => (
                      <button
                        onClick={onClick}
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                        title="OneDrive"
                      >
                        <Cloud size={14} className="text-blue-400" />
                      </button>
                    )}
                  />
                </div>
              </div>

              {/* File list */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {videoFiles.map((file, index) => (
                  <FileListItem
                    key={`video-${index}`}
                    file={file}
                    preview={videoPreviews[index]}
                    isSelected={selectedFileIndex === index}
                    onSelect={setSelectedFileIndex}
                    onRemove={handleRemoveFile}
                    index={index}
                  />
                ))}
                {csvFiles.map((file, index) => (
                  <FileListItem
                    key={`csv-${index}`}
                    file={file}
                    isSelected={false}
                    onSelect={() => {}}
                    onRemove={handleRemoveCsv}
                    index={index}
                  />
                ))}
              </div>

              <input
                type="file"
                ref={fileInputRef}
                accept="video/*,.csv"
                onChange={handleFileChange}
                className="hidden"
                multiple
              />
            </div>

            {/* Center panel - Video preview */}
            <div className="flex-1 min-w-0">
              <VideoPreviewPlayer
                file={videoFiles[selectedFileIndex]}
                previewUrl={videoPreviews[selectedFileIndex]}
                onRemove={() => handleRemoveFile(selectedFileIndex)}
              />
            </div>
          </>
          )
        ) : (
          /* YouTube input mode */
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <div className="w-full max-w-2xl space-y-4">
              {/* YouTube URL input */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Youtube size={20} className="text-red-500" />
                </div>
                <input
                  type="text"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl text-white placeholder-gray-500 border border-white/10 focus:outline-none focus:border-red-500/50 transition-all"
                  style={{ background: 'rgba(13, 13, 18, 0.8)' }}
                />
              </div>

              {/* URL validation */}
              {youtubeUrl && (
                <div className="flex items-center gap-2">
                  {youtubeUrl.includes('youtube.com') || youtubeUrl.includes('youtu.be') ? (
                    <>
                      <CheckCircle2 size={16} className="text-green-400" />
                      <span className="text-sm text-green-400">Valid YouTube URL</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={16} className="text-red-400" />
                      <span className="text-sm text-red-400">Please enter a valid YouTube URL</span>
                    </>
                  )}
                </div>
              )}

              {/* Tips */}
              <div className="p-4 rounded-xl border border-red-500/20"
                style={{ background: 'rgba(220, 38, 38, 0.08)' }}
              >
                <h4 className="text-sm font-medium text-red-400 mb-2">YouTube Requirements</h4>
                <ul className="text-xs text-gray-400 space-y-1">
                  <li>Video must be publicly available</li>
                  <li>Recommended: under 5 minutes for best results</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Right panel - Settings (optional) */}
        {inputType === 'upload' && videoFiles.length > 0 && (
          <div className="w-56 flex-shrink-0 flex flex-col rounded-xl border border-white/10 overflow-hidden"
            style={{ background: 'rgba(13, 13, 18, 0.6)', backdropFilter: 'blur(12px)' }}
          >
            <div className="px-3 py-2.5 border-b border-white/10">
              <span className="text-sm font-medium text-white">Settings</span>
            </div>
            <div className="flex-1 p-3 space-y-4">
              {/* Analysis mode info */}
              <div className="p-3 rounded-xl" style={{ background: `${currentMode.color}10`, border: `1px solid ${currentMode.color}30` }}>
                <div className="flex items-center gap-2 mb-2">
                  <ModeIcon size={16} style={{ color: currentMode.color }} />
                  <span className="text-sm font-medium" style={{ color: currentMode.color }}>{currentMode.name}</span>
                </div>
                <p className="text-xs text-gray-400">{currentMode.description}</p>
              </div>

              {/* Sample prompt inspiration dropdown */}
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 flex items-center gap-1">
                  <Sparkles size={10} className="text-[#FFD700]" />
                  Prompt Inspiration
                </label>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      const prompt = getDefaultPrompt(e.target.value);
                      setCustomInstructions(prompt);
                      e.target.value = ''; // Reset select after use
                    }
                  }}
                  className="w-full px-3 py-2 rounded-lg text-sm text-gray-400 border border-white/10 focus:outline-none cursor-pointer"
                  style={{ background: 'rgba(255, 255, 255, 0.03)' }}
                  defaultValue=""
                >
                  <option value="" disabled className="bg-gray-900">Select a template...</option>
                  {filteredModes.map(mode => (
                    <option key={mode.id} value={mode.id} className="bg-gray-900 text-white">
                      {mode.shortName || mode.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Custom instructions */}
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Custom Instructions</label>
                <textarea
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="Add specific analysis focus or select a template above..."
                  className="w-full px-3 py-2 rounded-lg text-sm text-white placeholder-gray-500 border border-white/10 focus:outline-none resize-none"
                  style={{ background: 'rgba(255, 255, 255, 0.03)', minHeight: '80px' }}
                />
              </div>

              {/* Stats */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Videos</span>
                  <span className="text-white font-mono">{videoFiles.length}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">CSV</span>
                  <span className="text-white font-mono">{csvFiles.length > 0 ? '1' : '0'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Total Size</span>
                  <span className="text-white font-mono">
                    {formatFileSize(videoFiles.reduce((acc, f) => acc + (f.size || 0), 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompactClassicAnalysis;
